import { useEffect, useState, useRef, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { supabase } from './supabase';
import './styles.css';
import Home from './pages/Home';
import Library from './pages/Library';
import Admin from './pages/Admin';
import Signup from './pages/Signup';
import SignupDonate from './pages/SignupDonate';
import SignupMember from './pages/SignupMember';
import SignupSupporter from './pages/SignupSupporter';
import SignupExpert from './pages/SignupExpert';
import Login from './pages/Login';
import About from './pages/About';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Song from './pages/Song';
import Search from './pages/Search';
import Donate from './pages/Donate';
import ThankYou from './pages/ThankYou';
import Profile from './pages/Profile';
import Subscribe from './pages/Subscribe';
import ChoirMusicBooks from './pages/ChoirMusicBooks';
import ChoirGames from './pages/ChoirGames';
import SelectionsManager from './pages/SelectionsManager';
import ChoirEvents from './pages/ChoirEvents';
import FloatingWidgets from './components/FloatingWidgets';


// Component to handle analytics, scroll, and visitor tracking
function RouteEnhancer({ user, setLastTracked, lastTracked }) {
  const location = useLocation();
  const navigate = useNavigate();

    useEffect(() => {
  // Only scroll to top if not Home, Library, Admin, or Blog Post page
  if (
    location.pathname !== '/' &&
    !location.pathname.startsWith('/library') &&
    location.pathname !== '/admin' &&
    !(
      location.pathname.startsWith('/blog/') && 
      location.pathname.split('/').length === 3 // matches "/blog/:permalink"
    )
  ) {
    window.scrollTo(0, 0);
  }

   // Google Analytics: trigger pageview on route change
  if (typeof window.gtag === 'function') {
    window.gtag('config', 'G-0VEW528YY9', {
      page_path: location.pathname + location.search + location.hash,
    });
  }


  const trackVisit = async () => {
      const pageUrl = location.pathname;
      const trackingKey = `${pageUrl}-${user?.id || 'anonymous'}`;
      const now = Date.now();
      const lastTrackedTime = lastTracked[trackingKey] || 0;

      if (now - lastTrackedTime < 60 * 60 * 1000) return;

      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      const userId = user?.id || null;
      console.log('Tracking visitor:', { pageUrl, userId, userAgent: navigator.userAgent });

      const trackingData = {
        pageUrl,
        userId,
        userAgent: navigator.userAgent,
      };

      try {
        const response = await fetch('/.netlify/functions/track-visitor', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` }),
          },
          body: JSON.stringify(trackingData),
        });
        const result = await response.json();
        if (!response.ok) {
          console.error('Track visit failed:', result.error || await response.text());
        } else {
          console.log('Visitor logged successfully:', result);
          setLastTracked((prev) => ({ ...prev, [trackingKey]: now }));
        }
      } catch (error) {
        console.error('Track visit error:', error.message);
      }
    };

    trackVisit();
  }, [location.pathname, user, navigate, lastTracked, setLastTracked]);

  return null;
}

// 404 Component
function NotFound() {
  return (
    <div className="container" style={{ textAlign: 'center', padding: '4rem 1rem' }}>
      <h1 className="hero-title" style={{ color: '#2f4f2f' }}>404 - Page Not Found</h1>
      <p className="hero-text">
        Oops! It looks like this page doesn’t exist. Let’s get you back on track.
      </p>
      <Link to="/" className="action-button">
        Return to Home
      </Link>
    </div>
  );
}

// Main App Content Component (MODIFIED for dynamic header)
function AppContent({
  user, setUser, isAdmin, setIsAdmin, userName, setUserName,
  loading, setLoading, recaptchaLoaded, setRecaptchaLoaded,
  lastTracked, setLastTracked
}) {
  const navigate = useNavigate();

  // DYNAMIC HEADER
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(60); // default, will update

  useLayoutEffect(() => {
    function updateHeaderHeight() {
      if (headerRef.current) setHeaderHeight(headerRef.current.offsetHeight);
    }
    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, [user, isAdmin, userName]);

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setIsAdmin(false);
    setUserName('');
    navigate('/');
  };

  const socialLinks = [
    { href: 'https://facebook.com/choircenter', icon: <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg> },
    { href: 'https://x.com/thechoircenter', icon: <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> },
    { href: 'https://wa.me/+2349150196438', icon: <svg className="social-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.36 5.07L2 22l4.93-1.36C8.42 21.5 10.15 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm3.97 15.03c-.25.7-.76 1.27-1.45 1.6-.7.34-1.6.5-2.52.25-1.8-.5-3.3-2-3.8-3.8-.25-.9-.08-1.8.25-2.52.33-.7.9-1.2 1.6-1.45.15-.05.3-.08.45-.08s.3.03.45.08c.35.15.6.45.7.8.1.35.15.7.15 1.05 0 .35-.05.7-.15 1.05-.5 1.5.5 2.5 1.5 3s2-.5 1.5-1.5c-.15-.35-.2-.7-.15-1.05s.2-.7.55-.85c.15-.05.3-.08.45-.08s.3.03.45.08z"/></svg> },
  ];

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    const email = e.target.email.value;
    if (!email) return;
    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .insert([{ email, subscribed_at: new Date().toISOString() }]);
      if (error) throw error;
      alert('Thanks for subscribing!');
      e.target.reset();
    } catch (error) {
      console.error('Newsletter signup error:', error.message);
      alert('Failed to subscribe. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <>
      <header
        ref={headerRef}
        className="fixed top-0 left-1/2 z-50 w-full max-w-full -translate-x-1/2 rounded-b-2xl
          bg-gradient-to-br from-green-900/90 via-black/90 to-green-700/90
          border-b-2 border-green-400 shadow-[0_4px_32px_2px_rgba(34,255,139,0.25)]
          backdrop-blur-md flex flex-col sm:flex-row items-center justify-between px-3 sm:px-6 py-1 sm:py-5 transition-all"
      >
        {/* ...navbar as before... */}
        <Link to="/" className="flex items-center gap-2 group mb-1 sm:mb-0">
          <img
            src="/logo.webp"
            alt="Choir Center Logo"
            className="h-8 w-8 sm:h-10 sm:w-10 rounded-full border-2 border-green-300 shadow-md"
          />
          <span className="text-xl sm:text-2xl font-extrabold bg-gradient-to-br from-green-400 via-lime-300 to-green-600 bg-clip-text text-transparent drop-shadow-[0_2px_8px_#34fa34b0]">
            Choir Center
          </span>
        </Link>
        <nav className="flex flex-row flex-wrap gap-1 sm:gap-4 items-center justify-center">
          {!user ? (
            <>
              <Link
                to="/signup"
                className="px-3 py-1 sm:px-4 sm:py-2 min-w-[80px] rounded-full font-semibold text-green-100 bg-black/30 hover:bg-green-500/30 transition shadow-sm border border-green-400/30 hover:shadow-green-400/30 text-center text-sm sm:text-base whitespace-nowrap"
              >
                Sign Up
              </Link>
              <Link
                to="/login"
                className="px-3 py-1 sm:px-4 sm:py-2 min-w-[80px] rounded-full font-semibold text-green-100 bg-black/30 hover:bg-green-500/30 transition shadow-sm border border-green-400/30 hover:shadow-green-400/30 text-center text-sm sm:text-base whitespace-nowrap"
              >
                Login
              </Link>
            </>
          ) : (
            <>
              {isAdmin && (
                <Link
                  to="/admin"
                  className="px-3 py-1 sm:px-4 sm:py-2 min-w-[120px] rounded-full font-semibold text-green-100 bg-black/30 hover:bg-green-500/30 transition shadow-sm border border-green-400/30 hover:shadow-green-400/30 text-center text-sm sm:text-base"
                >
                  Admin Dashboard
                </Link>
              )}
              <Link
                to="/profile"
                className="px-3 py-1 sm:px-4 sm:py-2 min-w-[90px] rounded-full font-semibold text-lime-300 bg-black/20 hover:bg-green-500/30 transition shadow border border-green-400/30 text-center text-sm sm:text-base whitespace-nowrap"
              >
                Hi, {userName}
              </Link>
              <button
                onClick={handleLogout}
                className="px-3 py-1 sm:px-4 sm:py-2 min-w-[80px] rounded-full font-semibold text-green-100 bg-gradient-to-br from-green-700/80 to-black/70 border border-green-400/30 shadow hover:bg-green-800/90 hover:text-neon transition text-sm sm:text-base whitespace-nowrap"
              >
                Logout
              </button>
            </>
          )}
        </nav>
      </header>

      {/* Dynamically padded content */}
      <div className="home-container min-h-screen" style={{ paddingTop: `${headerHeight}px` }}>
        <div className="background-notes">
          {/* ...background SVGs... */}
        </div>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/library" element={<Library />} />
          <Route path="/admin" element={<Admin />} />
          <Route
            path="/signup"
            element={user ? <Navigate to="/" /> : <Signup recaptchaLoaded={recaptchaLoaded} />}
          />
          <Route path="/signup-donate" element={<SignupDonate recaptchaLoaded={recaptchaLoaded} />} />
          <Route
            path="/login"
            element={user ? <Navigate to="/" /> : <Login recaptchaLoaded={recaptchaLoaded} />}
          />
          <Route
            path="/signup-member"
            element={user ? <Navigate to="/" /> : <SignupMember recaptchaLoaded={recaptchaLoaded} />}
          />
          <Route
            path="/signup-supporter"
            element={user ? <Navigate to="/" /> : <SignupSupporter recaptchaLoaded={recaptchaLoaded} />}
          />
          <Route
            path="/signup-expert"
            element={user ? <Navigate to="/" /> : <SignupExpert recaptchaLoaded={recaptchaLoaded} />}
          />
          <Route path="/about" element={<About />} />
          <Route path="/contact" element={<Contact recaptchaLoaded={recaptchaLoaded} />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:permalink" element={<BlogPost />} />
          <Route path="/song/:id" element={<Song />} />
          <Route path="/search" element={<Search />} />
          <Route path="/donate" element={<Navigate to="/subscribe" />} />
          <Route path="/subscribe" element={<Subscribe />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/profile" element={user ? <Profile user={user} /> : <Navigate to="/login" />} />
          <Route path="/choir-music-books" element={<ChoirMusicBooks />} />
          <Route path="/choir-games" element={<ChoirGames />} />
          <Route path="/selections-manager" element={<SelectionsManager />} />
          <Route path="/choir-events" element={<ChoirEvents />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        <FloatingWidgets />
        <RouteEnhancer user={user} setLastTracked={setLastTracked} lastTracked={lastTracked} />
      </div>

      {/* ...footer as before... */}
         <footer
          className="
            mt-20 mb-4 mx-auto w-[97vw] max-w-5xl rounded-2xl
            bg-gradient-to-br from-black/90 via-green-900/90 to-green-700/80
            border-2 border-green-500 shadow px-6 py-6
            text-center flex flex-col items-center gap-4
          "
        >
          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link to="/about" className="footer-link px-2 text-lime-200 hover:text-lime-400">About</Link>
            <Link to="/contact" className="footer-link px-2 text-lime-200 hover:text-lime-400">Contact</Link>
            <Link to="/privacy" className="footer-link px-2 text-lime-200 hover:text-lime-400">Privacy</Link>
            <Link to="/terms" className="footer-link px-2 text-lime-200 hover:text-lime-400">Terms</Link>
            <Link to="/blog" className="footer-link px-2 text-lime-200 hover:text-lime-400">Blog</Link>
          </div>
          <div className="footer-social flex gap-4 justify-center mt-2">
            <a href="https://facebook.com/choircenter" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
              <svg className="h-6 w-6 fill-lime-400" viewBox="0 0 24 24"><path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"/></svg>
            </a>
            <a href="https://x.com/thechoircenter" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">
              <svg className="h-6 w-6 fill-lime-400" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>
            <a href="https://wa.me/+2349150196438" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
              <svg className="h-6 w-6 fill-lime-400" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12c0 1.85.5 3.58 1.36 5.07L2 22l4.93-1.36C8.42 21.5 10.15 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2zm3.97 15.03c-.25.7-.76 1.27-1.45 1.6-.7.34-1.6.5-2.52.25-1.8-.5-3.3-2-3.8-3.8-.25-.9-.08-1.8.25-2.52.33-.7.9-1.2 1.6-1.45.15-.05.3-.08.45-.08s.3.03.45.08c.35.15.6.45.7.8.1.35.15.7.15 1.05 0 .35-.05.7-.15 1.05-.5 1.5.5 2.5 1.5 3s2-.5 1.5-1.5c-.15-.35-.2-.7-.15-1.05s.2-.7.55-.85c.15-.05.3-.08.45-.08s.3.03.45.08z"/></svg>
            </a>
          </div>
          <div className="text-lime-300 text-sm font-semibold tracking-wide mt-2">
            &copy; {new Date().getFullYear()} ChoirCenter. All rights reserved.
          </div>
        </footer>
    </>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [userName, setUserName] = useState('');
  const [loading, setLoading] = useState(true);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [lastTracked, setLastTracked] = useState({});

  useEffect(() => {
    const loadRecaptchaScript = () => {
      if (!document.querySelector('script[src="https://www.google.com/recaptcha/api.js?render=explicit"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.id = 'recaptcha-script';
        script.onload = () => {
          console.log('reCAPTCHA script loaded');
          setTimeout(() => setRecaptchaLoaded(true), 100);
        };
        script.onerror = () => {
          console.error('Failed to load reCAPTCHA script');
          setRecaptchaLoaded(false);
        };
        document.head.appendChild(script);
      } else {
        if (window.grecaptcha && window.grecaptcha.render) {
          setRecaptchaLoaded(true);
        }
      }
    };

    const loadGoogleAnalyticsScript = () => {
      if (!document.querySelector('script[src="https://www.googletagmanager.com/gtag/js?id=G-0VEW528YY9"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.googletagmanager.com/gtag/js?id=G-0VEW528YY9';
        script.async = true;
        script.onload = () => initGoogleAnalytics();
        document.head.appendChild(script);
      }
    };

    loadRecaptchaScript();
    loadGoogleAnalyticsScript();

    const fetchUserAndProfile = async () => {
      setLoading(true);
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        setUser(null);
        setIsAdmin(false);
        setUserName('');
        setLoading(false);
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.getUser();
      if (authError) {
        setUser(null);
        setIsAdmin(false);
        setUserName('');
        setLoading(false);
        return;
      }
      const currentUser = authData.user;
      setUser(currentUser);

      if (currentUser) {
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('is_admin, full_name')
          .eq('id', currentUser.id)
          .limit(1)
          .maybeSingle();
        if (profileError) {
          setIsAdmin(false);
          setUserName('');
        } else {
          setIsAdmin(profileData?.is_admin || false);
          setUserName(profileData?.full_name || currentUser.email.split('@')[0]);
        }
      }
      setLoading(false);
    };
    fetchUserAndProfile();

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      const currentUser = session?.user || null;
      setUser(currentUser);
      if (event === 'SIGNED_IN' && currentUser && session?.user?.email_confirmed_at) {
        supabase
          .from('profiles')
          .select('is_admin, full_name')
          .eq('id', currentUser.id)
          .limit(1)
          .maybeSingle()
          .then(({ data, error }) => {
            if (error) {
              setIsAdmin(false);
              setUserName('');
            } else {
              setIsAdmin(data?.is_admin || false);
              setUserName(data?.full_name || currentUser.email.split('@')[0]);
            }
          });
      } else {
        setIsAdmin(false);
        setUserName('');
      }
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <AppContent
          user={user}
          setUser={setUser}
          isAdmin={isAdmin}
          setIsAdmin={setIsAdmin}
          userName={userName}
          setUserName={setUserName}
          loading={loading}
          setLoading={setLoading}
          recaptchaLoaded={recaptchaLoaded}
          setRecaptchaLoaded={setRecaptchaLoaded}
          lastTracked={lastTracked}
          setLastTracked={setLastTracked}
        />
      </Router>
    </HelmetProvider>
  );
}

export default App;
